<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { fetchAiConfig, updateAiConfig } from '../api';

const { t } = useI18n();
const store = useStore();

const accountId = computed(() => store.state.route?.params?.accountId);
const isAdmin = computed(() => store.getters.getCurrentRole === 'administrator');

const loading = ref(true);
const saving = ref(false);
const errorMsg = ref('');
const savedMsg = ref('');

// Mirrors the account_ai_configs contract (PRD §3.4).
const form = ref({
  enabled: false,
  welcome_enabled: true,
  welcome_message: '',
  agent_rules: '',
  auto_reply_stop_rules: '',
  auto_reply_stop_rules_default: '',
  kb_search_enabled: true,
  full_image_read: false,
  voice_transcribe_auto: true,
  voice_fallback_text: '',
  handoff: { max_rounds: 10 },
});

const DEFAULT_HANDOFF_MAX_ROUNDS = 10;

const load = async () => {
  loading.value = true;
  errorMsg.value = '';
  try {
    const data = await fetchAiConfig(accountId.value);
    form.value = {
      enabled: !!data.enabled,
      welcome_enabled: data.welcome_enabled !== false,
      welcome_message: data.welcome_message || '',
      agent_rules: data.agent_rules || '',
      auto_reply_stop_rules:
        (data.auto_reply_stop_rules || '').trim() ||
        data.auto_reply_stop_rules_default ||
        '',
      auto_reply_stop_rules_default: data.auto_reply_stop_rules_default || '',
      kb_search_enabled: data.kb_search_enabled !== false,
      full_image_read: !!data.full_image_read,
      voice_transcribe_auto: data.voice_transcribe_auto !== false,
      voice_fallback_text: data.voice_fallback_text || '',
      handoff: { max_rounds: data.handoff?.max_rounds ?? DEFAULT_HANDOFF_MAX_ROUNDS },
    };
  } catch (err) {
    errorMsg.value = err?.response?.data?.error || t('CUSTOM.COMMON.LOAD_FAILED');
  } finally {
    loading.value = false;
  }
};

const save = async () => {
  saving.value = true;
  errorMsg.value = '';
  savedMsg.value = '';
  try {
    if (
      !form.value.voice_transcribe_auto &&
      !(form.value.voice_fallback_text || '').trim()
    ) {
      errorMsg.value = t('CUSTOM.AI_AGENT.VOICE_FALLBACK_REQUIRED');
      saving.value = false;
      return;
    }
    const maxRounds = Number(form.value.handoff.max_rounds);
    const data = await updateAiConfig(accountId.value, {
      enabled: form.value.enabled,
      welcome_enabled: form.value.welcome_enabled,
      welcome_message: form.value.welcome_message,
      agent_rules: form.value.agent_rules,
      auto_reply_stop_rules: form.value.auto_reply_stop_rules,
      kb_search_enabled: form.value.kb_search_enabled,
      full_image_read: form.value.full_image_read,
      voice_transcribe_auto: form.value.voice_transcribe_auto,
      voice_fallback_text: form.value.voice_fallback_text,
      handoff: {
        max_rounds:
          Number.isFinite(maxRounds) && maxRounds >= 0
            ? maxRounds
            : DEFAULT_HANDOFF_MAX_ROUNDS,
      },
    });
    form.value.auto_reply_stop_rules =
      (data.auto_reply_stop_rules || '').trim() ||
      data.auto_reply_stop_rules_default ||
      form.value.auto_reply_stop_rules;
    form.value.auto_reply_stop_rules_default =
      data.auto_reply_stop_rules_default || form.value.auto_reply_stop_rules_default;
    savedMsg.value = t('CUSTOM.COMMON.SAVED');
  } catch (err) {
    errorMsg.value = err?.response?.data?.errors?.join(', ') || t('CUSTOM.COMMON.SAVE_FAILED');
  } finally {
    saving.value = false;
  }
};

const restoreStopRulesDefault = () => {
  form.value.auto_reply_stop_rules = form.value.auto_reply_stop_rules_default || '';
};

onMounted(() => {
  if (isAdmin.value) load();
  else loading.value = false;
});
</script>

<template>
  <!-- h-full + overflow-y-auto: dashboard shell is fixed-height; page must scroll itself. -->
  <div class="aia-scroll">
  <div class="aia-page">
    <header class="aia-header">
      <div class="aia-header-text">
        <h1 class="aia-title">{{ t('CUSTOM.AI_AGENT.TITLE') }}</h1>
        <p class="aia-sub">
          {{ t('CUSTOM.AI_AGENT.SUBTITLE') }}
        </p>
      </div>
      <div v-if="isAdmin && !loading" class="aia-header-actions">
        <span v-if="savedMsg" class="aia-ok">{{ savedMsg }}</span>
        <span v-if="errorMsg" class="aia-err">{{ errorMsg }}</span>
        <button type="button" class="aia-btn" :disabled="saving" @click="save">
          {{ saving ? t('CUSTOM.COMMON.SAVING') : t('CUSTOM.COMMON.SAVE') }}
        </button>
      </div>
    </header>

    <div v-if="!isAdmin" class="aia-notice">{{ t('CUSTOM.COMMON.ADMIN_ONLY') }}</div>
    <div v-else-if="loading" class="aia-notice">{{ t('CUSTOM.COMMON.LOADING') }}</div>

    <form v-else class="aia-form" @submit.prevent="save">
      <section class="aia-card">
        <label class="aia-switch">
          <input v-model="form.enabled" type="checkbox" />
          <span class="aia-switch-label">
            <strong>{{ t('CUSTOM.AI_AGENT.ENABLED') }}</strong>
            <em>{{ t('CUSTOM.AI_AGENT.ENABLED_HINT') }}</em>
          </span>
        </label>
      </section>

      <section class="aia-card aia-card--rules">
        <div class="aia-field-title">{{ t('CUSTOM.AI_AGENT.RULES_TITLE') }}</div>
        <p class="aia-hint">{{ t('CUSTOM.AI_AGENT.RULES_HINT') }}</p>
        <textarea
          v-model="form.agent_rules"
          class="aia-textarea aia-textarea--rules"
          rows="16"
          :placeholder="t('CUSTOM.AI_AGENT.RULES_PLACEHOLDER')"
        />
      </section>

      <div class="aia-grid">
      <section class="aia-card">
        <label class="aia-switch">
          <input v-model="form.welcome_enabled" type="checkbox" />
          <span class="aia-switch-label">
            <strong>{{ t('CUSTOM.AI_AGENT.WELCOME_ENABLED') }}</strong>
            <em>{{ t('CUSTOM.AI_AGENT.WELCOME_ENABLED_HINT') }}</em>
          </span>
        </label>
        <textarea
          v-model="form.welcome_message"
          class="aia-textarea"
          rows="4"
          :placeholder="t('CUSTOM.AI_AGENT.WELCOME_PLACEHOLDER')"
        />
      </section>

      <section class="aia-card">
        <label class="aia-switch">
          <input v-model="form.kb_search_enabled" type="checkbox" />
          <span class="aia-switch-label">
            <strong>{{ t('CUSTOM.AI_AGENT.KB_SEARCH_ENABLED') }}</strong>
            <em>{{ t('CUSTOM.AI_AGENT.KB_SEARCH_ENABLED_HINT') }}</em>
          </span>
        </label>
      </section>

      <section class="aia-card">
        <label class="aia-switch">
          <input v-model="form.full_image_read" type="checkbox" />
          <span class="aia-switch-label">
            <strong>{{ t('CUSTOM.AI_AGENT.FULL_IMAGE_READ') }}</strong>
            <em>{{ t('CUSTOM.AI_AGENT.FULL_IMAGE_READ_HINT') }}</em>
          </span>
        </label>
      </section>

      <section class="aia-card">
        <label class="aia-switch">
          <input v-model="form.voice_transcribe_auto" type="checkbox" />
          <span class="aia-switch-label">
            <strong>{{ t('CUSTOM.AI_AGENT.VOICE_TRANSCRIBE_AUTO') }}</strong>
            <em>{{ t('CUSTOM.AI_AGENT.VOICE_TRANSCRIBE_AUTO_HINT') }}</em>
          </span>
        </label>
        <div class="aia-field-title" style="margin-top: 12px">
          {{ t('CUSTOM.AI_AGENT.VOICE_FALLBACK_TITLE') }}
        </div>
        <p class="aia-hint">{{ t('CUSTOM.AI_AGENT.VOICE_FALLBACK_HINT') }}</p>
        <textarea
          v-model="form.voice_fallback_text"
          class="aia-textarea"
          rows="3"
          :placeholder="t('CUSTOM.AI_AGENT.VOICE_FALLBACK_PLACEHOLDER')"
        />
      </section>

      <section class="aia-card">
        <div class="aia-field-title">{{ t('CUSTOM.AI_AGENT.MAX_ROUNDS_TITLE') }}</div>
        <p class="aia-hint">
          {{ t('CUSTOM.AI_AGENT.MAX_ROUNDS_HINT') }}
        </p>
        <input v-model="form.handoff.max_rounds" type="number" min="0" max="50" class="aia-number" />
      </section>
      </div>

      <section class="aia-card aia-card--rules">
        <div class="aia-field-title">{{ t('CUSTOM.AI_AGENT.STOP_RULES_TITLE') }}</div>
        <p class="aia-hint">{{ t('CUSTOM.AI_AGENT.STOP_RULES_HINT') }}</p>
        <textarea
          v-model="form.auto_reply_stop_rules"
          class="aia-textarea aia-textarea--stop-rules"
          rows="8"
          :placeholder="t('CUSTOM.AI_AGENT.STOP_RULES_PLACEHOLDER')"
        />
        <button
          type="button"
          class="aia-link-btn"
          @click="restoreStopRulesDefault"
        >
          {{ t('CUSTOM.AI_AGENT.STOP_RULES_RESET') }}
        </button>
      </section>

      <button type="submit" class="aia-submit-hidden" :disabled="saving" aria-hidden="true" tabindex="-1" />
    </form>
  </div>
  </div>
</template>

<style scoped>
/* Colors use the host's next-color CSS variables (rgb triplets) so the page
   follows light/dark theme automatically — no hardcoded hex (PRD §9.6). */
.aia-scroll { height: 100%; width: 100%; overflow-y: auto; box-sizing: border-box; }
.aia-page {
  width: 100%;
  max-width: none;
  min-height: 100%;
  padding: 0 clamp(16px, 2.5vw, 40px) clamp(32px, 4vh, 56px);
  box-sizing: border-box;
}
.aia-header {
  position: sticky; top: 0; z-index: 2; display: flex; align-items: flex-start; gap: 16px;
  margin: 0 0 20px; padding: clamp(14px, 2vh, 20px) 0;
  background: rgb(var(--slate-1)); border-bottom: 1px solid rgb(var(--slate-5));
}
.aia-header-text { flex: 1; min-width: 0; }
.aia-header-actions { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
.aia-title { font-size: clamp(18px, 1.4vw, 22px); font-weight: 700; color: rgb(var(--slate-12)); margin: 0 0 6px; }
.aia-sub { font-size: 14px; line-height: 1.65; color: rgb(var(--slate-11)); margin: 0; max-width: 72ch; }
.aia-notice { font-size: 14px; color: rgb(var(--slate-11)); padding: 24px 0; }
.aia-form { display: flex; flex-direction: column; gap: 16px; width: 100%; }
.aia-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 320px), 1fr));
  gap: 16px;
  align-items: start;
}
.aia-card { border: 1px solid rgb(var(--slate-5)); background: rgb(var(--slate-1)); border-radius: 10px;
  padding: clamp(16px, 1.5vw, 22px) clamp(18px, 1.8vw, 24px); }
.aia-card--rules { display: flex; flex-direction: column; min-height: 0; }
.aia-field-title { font-weight: 600; color: rgb(var(--slate-12)); font-size: 15px; margin-bottom: 6px; }
.aia-hint { font-size: 13px; line-height: 1.65; color: rgb(var(--slate-11)); margin: 0 0 12px; }
.aia-switch { display: flex; align-items: flex-start; gap: 12px; cursor: pointer; }
.aia-switch input { width: 18px; height: 18px; margin-top: 3px; cursor: pointer; flex-shrink: 0;
  accent-color: rgb(var(--blue-9)); }
.aia-switch-label { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.aia-switch-label strong { font-size: 15px; color: rgb(var(--slate-12)); }
.aia-switch-label em { font-size: 13px; color: rgb(var(--slate-11)); font-style: normal; line-height: 1.55; }
.aia-textarea { width: 100%; margin-top: 12px; border: 1px solid rgb(var(--slate-7)); border-radius: 8px;
  padding: 12px 14px; font-size: 14px; line-height: 1.65; color: rgb(var(--slate-12));
  background: rgb(var(--slate-1)); box-sizing: border-box; resize: vertical; min-height: 96px; }
.aia-textarea--rules {
  flex: 1;
  min-height: min(52vh, 640px);
  margin-top: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px;
}
.aia-textarea--stop-rules {
  margin-top: 0;
  min-height: 180px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px;
}
.aia-link-btn {
  align-self: flex-start;
  margin-top: 10px;
  border: none;
  background: none;
  padding: 0;
  font-size: 13px;
  color: rgb(var(--blue-9));
  cursor: pointer;
}
.aia-number { width: 112px; border: 1px solid rgb(var(--slate-7)); border-radius: 8px; padding: 10px 12px;
  font-size: 15px; color: rgb(var(--slate-12)); background: rgb(var(--slate-1)); }
.aia-submit-hidden { display: none; }
.aia-btn { background: rgb(var(--blue-9)); color: #fff; border: none; border-radius: 8px; padding: 10px 24px;
  font-size: 14px; font-weight: 600; cursor: pointer; }
.aia-btn:disabled { opacity: .6; cursor: default; }
.aia-ok { color: rgb(var(--grass-11, 22 163 74)); font-size: 13px; }
.aia-err { color: rgb(var(--ruby-11, 220 38 38)); font-size: 13px; }
</style>
